export type ClientExceptionFingerprintInput = {
  code?: unknown;
  error_category?: unknown;
  http_status?: unknown;
  request_path?: unknown;
  step?: unknown;
  surface?: unknown;
};

const FINGERPRINT_VERSION = 'app-error:v1';
const LONG_ID_SEGMENT_PATTERN = /^(?:[0-9a-f]{16,}|[a-z0-9_-]{20,})$/i;
const UUID_SEGMENT_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PROJECT_ROOT_STATIC_SEGMENTS = new Set(['stats', 'test-connection']);
const PROJECT_STATIC_NAMESPACES = new Set([
  'collections',
  'runs',
  'source-auth',
  'source-connections',
  'source-preview',
  'source-resources',
]);
const PROJECT_RUN_STATIC_SEGMENTS = new Set(['active', 'active-summary']);
const FIELD_STATIC_SEGMENTS = new Set(['bulk', 'usage']);
const DYNAMIC_RESOURCE_SEGMENTS = new Map([
  ['configs', ':config'],
  ['projects', ':project'],
  ['runs', ':run'],
  ['users', ':user'],
]);

function normalizeString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeFingerprintToken(value: unknown): string | undefined {
  const normalized = normalizeString(value)
    ?.toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return normalized || undefined;
}

function requestPathname(value: unknown): string | undefined {
  const normalized = normalizeString(value);
  if (!normalized) {
    return undefined;
  }

  try {
    return new URL(normalized).pathname;
  } catch {
    return normalized.split(/[?#]/, 1)[0] || undefined;
  }
}

function normalizeDynamicSegments(pathname: string): string {
  const segments = pathname.split('/');

  for (let index = 1; index < segments.length; index += 1) {
    const segment = segments[index];
    const previousSegment = segments[index - 1]?.toLowerCase();
    const replacement = previousSegment
      ? DYNAMIC_RESOURCE_SEGMENTS.get(previousSegment)
      : undefined;

    if (replacement) {
      segments[index] = replacement;
      continue;
    }

    if (segment && (UUID_SEGMENT_PATTERN.test(segment) || LONG_ID_SEGMENT_PATTERN.test(segment))) {
      segments[index] = ':id';
    }
  }

  return segments.join('/');
}

function normalizeProjectRoute(pathname: string): string | undefined {
  const segments = pathname.split('/');
  if (segments[1]?.toLowerCase() !== 'api' || segments[2]?.toLowerCase() !== 'projects') {
    return undefined;
  }

  const projectIndex = 3;
  const projectSegment = segments[projectIndex];
  if (!projectSegment) {
    return pathname;
  }

  if (PROJECT_ROOT_STATIC_SEGMENTS.has(projectSegment.toLowerCase())) {
    return pathname;
  }

  segments[projectIndex] = ':project';
  const namespaceIndex = projectIndex + 1;
  const namespace = segments[namespaceIndex]?.toLowerCase();
  if (!namespace) {
    return segments.join('/');
  }

  if (namespace === 'collections') {
    if (segments[namespaceIndex + 1]) {
      segments[namespaceIndex + 1] = ':collection';
    }
  } else if (namespace === 'runs') {
    const runSegment = segments[namespaceIndex + 1];
    if (runSegment && !PROJECT_RUN_STATIC_SEGMENTS.has(runSegment.toLowerCase())) {
      segments[namespaceIndex + 1] = ':run';
    }
  } else if (!PROJECT_STATIC_NAMESPACES.has(namespace)) {
    segments[namespaceIndex] = ':collection';
  }

  for (let index = namespaceIndex + 1; index < segments.length; index += 1) {
    const segment = segments[index];
    const previousSegment = segments[index - 1]?.toLowerCase();

    if (
      previousSegment === 'fields' &&
      segment &&
      !FIELD_STATIC_SEGMENTS.has(segment.toLowerCase())
    ) {
      segments[index] = ':field';
      continue;
    }

    if (segment && (UUID_SEGMENT_PATTERN.test(segment) || LONG_ID_SEGMENT_PATTERN.test(segment))) {
      segments[index] = ':id';
    }
  }

  return segments.join('/');
}

export function normalizeClientErrorRequestFamily(value: unknown): string | undefined {
  const pathname = requestPathname(value);
  if (!pathname) {
    return undefined;
  }

  return normalizeProjectRoute(pathname) ?? normalizeDynamicSegments(pathname);
}

export function buildClientExceptionFingerprint(
  input: ClientExceptionFingerprintInput,
): string | undefined {
  const surface = normalizeFingerprintToken(input.surface);
  const step = normalizeFingerprintToken(input.step);
  if (!surface || !step) {
    return undefined;
  }

  const code = normalizeFingerprintToken(input.code);
  const category = normalizeFingerprintToken(input.error_category);
  const status =
    typeof input.http_status === 'number' && Number.isFinite(input.http_status)
      ? String(input.http_status)
      : undefined;
  const requestFamily = normalizeClientErrorRequestFamily(input.request_path);

  if (!code && (!category || category === 'unknown') && !status && !requestFamily) {
    return undefined;
  }

  const fields = [
    ['surface', surface],
    ['step', step],
    ['category', category],
    ['code', code],
    ['status', status],
    ['request', requestFamily],
  ]
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`);

  return `${FINGERPRINT_VERSION}|${fields.join('|')}`;
}
